import { createSignal } from 'solid-js';
import { streamingService, StreamingDataPoint } from '../services/streamingService';
import { streamingDataService } from '../services/streamingDataService';
import { sourcesStore } from './sourcesStore';
import { selectedTime, timeWindow, getDisplayWindowReferenceTime } from './playbackStore';
import { selectedRange, selectedRanges } from './selectionStore';
import { LIVE_STREAM_CHANNELS } from '../constants/liveChannels';
import { debug, warn, error as logError } from '../utils/console';
import { config } from '../config/env';
import { liveConfigStore } from './liveConfigStore';
import {
  computeLiveTrackTailQueryWindow,
  computeTrackSampleCadence,
  dedupeTrackPointsByTimestamp,
} from '../utils/liveTrackSampleCadence';

/**
 * Streaming Store
 * WebSocket-only store for managing streaming data connections
 * Emits updates immediately as websocket data arrives (no batching for maximum responsiveness)
 *
 * "FROM REDIS" IS A MISNOMER THROUGHOUT THIS FILE. Redis was retired in Phase D; every
 * `*FromRedis` method here is an HTTP fetch against `/api/stream/sources/:name/data[/merged]`,
 * which the stream server answers from its in-memory recentStore and falls back to durable Parquet
 * (via server_file) for older ranges. The response shape is unchanged from the Redis era, which is
 * why the names survived. Read "Redis" in this file as "the live history REST endpoint".
 *
 * ALSO NOTE this whole store is the LEGACY path. The live map defaults to the columnar Arrow route
 * (`config.LIVE_MAP_GPU`, on unless explicitly disabled), where `LiveMapContainer` returns before
 * `initialize()` is ever called and the legacy JSON WebSocket server is retired. In that mode
 * nothing here receives WebSocket data, so `isWebSocketRecentlyActive()` answers false forever —
 * callers must not read that as "the live feed is quiet".
 */
class StreamingStore {
  private _isInitialized = false;
  private _isInitializing = false;
  private unsubscribeCallbacks: Array<() => void> = [];
  // Track sources we've already warned about to avoid spam
  private sourceIdMismatchWarned = new Set<string>();
  private sourceIdLookupWarned = new Set<string>();
  private sourceIdMissingWarned = new Set<string>();
  // Counter to reduce logging frequency
  private dataPointCount = 0;
  /** WebSocket points dropped by the handler's catch - see handleWebSocketData. */
  private wsHandlingErrorCount = 0;
  /** Dedupe parallel connect() attempts during subscription retries. */
  private sharedConnectPromise: Promise<boolean> | null = null;

  // Reactive signals
  private loadingSignal = createSignal<boolean>(false);
  private errorSignal = createSignal<string | null>(null);
  
  // Reactive signal for immediate data updates (components can watch this)
  // This stores only the latest point per source for immediate updates
  private newDataSignal = createSignal<Map<number, any[]>>(new Map());
  
  // Signal bumped when historical data is loaded over REST - triggers timeline re-render
  private historicalDataVersionSignal = createSignal(0);
  // Signal bumped on every WebSocket append to historicalData - ensures chart/map re-run when getNewData() is cleared
  private liveDataAppendVersionSignal = createSignal(0);
  /** Bumped on the slow live-map track refresh (history merge) — not on each WebSocket append. */
  private trackDataVersionSignal = createSignal(0);
  /** Bumped on cleanup() so live views can re-run initialize() while still mounted. */
  private streamingLifecycleSignal = createSignal(0);

  // Historical data storage - accumulates all data points per source
  // This is used for time window filtering and track rendering
  private historicalData = new Map<number, any[]>();

  /**
   * Append-only catalog for live map rendering.
   * A history fetch may seed/prepend old context, but the visible live tail only advances from WebSocket.
   */
  private liveRenderData = new Map<number, any[]>();

  /**
   * History-only catalog for live map track drawing.
   * WebSocket samples never write here, so track cadence reflects the REST/Influx query cadence.
   */
  private liveTrackData = new Map<number, any[]>();

  // Source IDs we are currently subscribed to (avoids cleanup when table/map both call initialize with same ids)
  private currentSourceIds = new Set<number>();

  /** Coalesce overlapping initial history loads (same sources + window + ~1s end bucket) */
  private redisLoadInflight = new Map<string, Promise<void>>();

  /** Coalesce WebSocket data_batch into one reactive flush (avoids 12× map redraw per batch) */
  private reactiveBatchDepth = 0;
  private batchPendingNewData: Map<number, any[]> | null = null;
  private batchPendingLiveAppend = false;
  /**
   * Skip redundant playback-window pulls while the WebSocket is actively appending.
   * Stamped ONLY by the legacy WS emit path, so it stays 0 for the whole session in gpuMode.
   */
  private lastWebSocketAppendAt = 0;

  /** Single-flight guard for the live map track history refresh. */
  private liveTrackRefreshInFlight: Promise<void> | null = null;
  private pendingLiveTrackRefresh: {
    sourceIds: Set<number>;
    queryEndMs: number;
  } | null = null;
  /** Single-flight guard for the processing-mode boat backfill. */
  private liveBoatBackfillInFlight: Promise<void> | null = null;
  private pendingLiveBoatBackfill: {
    sourceIds: Set<number>;
    queryEndMs: number;
  } | null = null;
  /** Wall-clock time when the last live-map boat backfill finished (throttle + defer pending). */
  private lastLiveMapBackfillCompletedAt = 0;
  private liveBoatBackfillDeferredTimer: ReturnType<typeof setTimeout> | null = null;
  private lastTrackRefreshSkippedLogAt = 0;
  private trackRefreshBeforeSeedWarned = new Set<number>();
  private lastTrackRefreshDiagnostics: Array<{
    sourceId: number;
    queriedStartMs: number;
    queriedEndMs: number;
    addedPoints: number;
    returnedPoints: number;
    duplicatePointsSkipped: number;
    minDeltaMs: number | null;
    maxDeltaMs: number | null;
    approxHz: number | null;
    skippedEmptyCatalog: boolean;
  }> = [];

  private static lowerBoundByTimestamp(arr: any[], targetMs: number): number {
    let lo = 0;
    let hi = arr.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (StreamingStore.pointTimestampMs(arr[mid]) < targetMs) lo = mid + 1;
      else hi = mid;
    }
    return lo;
  }

  private static upperBoundByTimestamp(arr: any[], targetMs: number): number {
    let lo = 0;
    let hi = arr.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (StreamingStore.pointTimestampMs(arr[mid]) <= targetMs) lo = mid + 1;
      else hi = mid;
    }
    return lo;
  }

  private static setsEqual(a: Set<number>, b: Set<number>): boolean {
    if (a.size !== b.size) return false;
    for (const x of a) if (!b.has(x)) return false;
    return true;
  }

  /** Timestamp in ms for sorting / dedupe (sorted arrays per source). */
  private static pointTimestampMs(d: any): number {
    if (!d) return 0;
    const ts = d.timestamp || (d.Datetime instanceof Date ? d.Datetime.getTime() : new Date(d.Datetime).getTime());
    return Number.isFinite(ts) ? ts : 0;
  }

  /** GPS channel names — prefer *_dd / canonical names over generic Lat/Lng when resolving. */
  private static readonly GPS_LAT_KEYS = [
    'Lat_dd',
    'lat_dd',
    'LATITUDE_GPS_unk',
    'latitude_gps_unk',
    'Lat',
    'lat',
  ] as const;

  private static readonly GPS_LNG_KEYS = [
    'Lng_dd',
    'lng_dd',
    'LONGITUDE_GPS_unk',
    'longitude_gps_unk',
    'Lng',
    'lng',
  ] as const;

  private static readonly GPS_CHANNEL_NAMES = new Set<string>([
    ...StreamingStore.GPS_LAT_KEYS,
    ...StreamingStore.GPS_LNG_KEYS,
  ]);

  /** Resolve lat/lng preferring canonical GPS channels (Lat_dd) over stale generic Lat/Lng. */
  private static resolvePointCoordinates(point: Record<string, unknown>): {
    lat: number;
    lng: number;
  } | null {
    let latRaw: unknown;
    for (const key of StreamingStore.GPS_LAT_KEYS) {
      const v = point[key];
      if (v != null && v !== '') {
        latRaw = v;
        break;
      }
    }
    let lngRaw: unknown;
    for (const key of StreamingStore.GPS_LNG_KEYS) {
      const v = point[key];
      if (v != null && v !== '') {
        lngRaw = v;
        break;
      }
    }
    const latN = latRaw != null ? Number(latRaw) : NaN;
    const lngN = lngRaw != null ? Number(lngRaw) : NaN;
    if (!Number.isFinite(latN) || !Number.isFinite(lngN)) return null;
    if (Math.abs(latN) < 1e-6 && Math.abs(lngN) < 1e-6) return null;
    return { lat: latN, lng: lngN };
  }

  /** Keep all lat/lng alias fields aligned on one coordinate pair. */
  private static syncPointCoordinates(point: Record<string, unknown>): void {
    const resolved = StreamingStore.resolvePointCoordinates(point);
    if (!resolved) return;
    const { lat: latN, lng: lngN } = resolved;
    point.Lat = latN;
    point.Lng = lngN;
    point.Lat_dd = latN;
    point.Lng_dd = lngN;
    point.lat = latN;
    point.lng = lngN;
    point.lat_dd = latN;
    point.lng_dd = lngN;
    point.LATITUDE_GPS_unk = latN;
    point.LONGITUDE_GPS_unk = lngN;
  }

  private static numericChannelValue(value: unknown): number | undefined {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : undefined;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed === '') return undefined;
      const n = Number(trimmed);
      return Number.isFinite(n) ? n : undefined;
    }
    return undefined;
  }

  /**
   * Live packet loss sometimes arrives as numeric sentinels instead of nulls.
   * Treat zero/negative boat-speed samples as missing so charts do not draw
   * artificial drops, and overlays keep looking back to the last valid BSP.
   */
  private static numericLiveChannelValue(channel: string, value: unknown): number | undefined {
    const n = StreamingStore.numericChannelValue(value);
    if (n === undefined) return undefined;

    const normalized = channel.toLowerCase();
    if (
      (normalized.includes('bsp') ||
        normalized.includes('sog') ||
        normalized.includes('vmg')) &&
      n <= 0
    ) {
      return undefined;
    }

    return n;
  }

  private static incomingPayloadHasGps(data: Record<string, unknown> | undefined): boolean {
    if (!data) return false;
    for (const key of Object.keys(data)) {
      if (
        StreamingStore.GPS_CHANNEL_NAMES.has(key) &&
        StreamingStore.numericLiveChannelValue(key, data[key]) !== undefined
      ) {
        return true;
      }
    }
    return false;
  }

  private static sortPointsByTimestamp(points: any[]): any[] {
    return [...points].sort(
      (a, b) => StreamingStore.pointTimestampMs(a) - StreamingStore.pointTimestampMs(b)
    );
  }

  /** Merge two timestamp-sorted arrays; skip duplicate timestamps. */
  private static mergeSortedTrackPointsDedupe(
    existing: any[],
    incoming: any[]
  ): { merged: any[]; added: number } {
    const sortedIncoming = StreamingStore.sortPointsByTimestamp(incoming);
    if (existing.length === 0) {
      return { merged: sortedIncoming, added: sortedIncoming.length };
    }
    if (sortedIncoming.length === 0) {
      return { merged: existing, added: 0 };
    }

    const merged: any[] = [];
    let i = 0;
    let j = 0;
    let added = 0;

    while (i < existing.length || j < sortedIncoming.length) {
      if (j >= sortedIncoming.length) {
        merged.push(existing[i++]);
        continue;
      }
      if (i >= existing.length) {
        merged.push(sortedIncoming[j++]);
        added++;
        continue;
      }

      const existingTs = StreamingStore.pointTimestampMs(existing[i]);
      const incomingTs = StreamingStore.pointTimestampMs(sortedIncoming[j]);
      if (existingTs < incomingTs) {
        merged.push(existing[i++]);
      } else if (existingTs > incomingTs) {
        merged.push(sortedIncoming[j++]);
        added++;
      } else {
        merged.push(existing[i++]);
        j++;
      }
    }

    return { merged, added };
  }

  /** Rolling retention for live historicalData — window + buffer + small margin. */
  private getHistoricalRetentionMs(): number {
    const twMin = Number(timeWindow());
    const windowMs = twMin > 0 ? twMin * 60 * 1000 : 5 * 60 * 1000;
    const bufferMs = liveConfigStore.bufferMs();
    const dataDelayMs = liveConfigStore.dataDelayMs();
    const marginMs = 60 * 1000;
    return Math.max(windowMs + bufferMs + dataDelayMs + marginMs, 3 * 60 * 1000);
  }

  /** True when WebSocket appended data within the given window (ms). */
  isWebSocketRecentlyActive(withinMs: number = 6000): boolean {
    return this.lastWebSocketAppendAt > 0 && Date.now() - this.lastWebSocketAppendAt < withinMs;
  }

  /** Insert in timestamp order; returns false when duplicate. */
  private insertHistoricalPoint(arr: any[], point: any): boolean {
    const ts = StreamingStore.pointTimestampMs(point);
    let lo = 0;
    let hi = arr.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      const midTs = StreamingStore.pointTimestampMs(arr[mid]);
      if (midTs === ts) return false;
      if (midTs < ts) lo = mid + 1;
      else hi = mid;
    }
    arr.splice(lo, 0, point);
    return true;
  }

  /** Drop points older than retention window (arrays are sorted by timestamp). */
  private trimHistoricalSource(sourceId: number): void {
    const arr = this.historicalData.get(sourceId);
    if (!arr || arr.length === 0) return;
    const lastTs = StreamingStore.pointTimestampMs(arr[arr.length - 1]);
    const ref = lastTs > 0 ? lastTs : Date.now();
    const cutoff = ref - this.getHistoricalRetentionMs();
    let startIdx = 0;
    while (startIdx < arr.length && StreamingStore.pointTimestampMs(arr[startIdx]) < cutoff) {
      startIdx++;
    }
    if (startIdx > 0) {
      arr.splice(0, startIdx);
    }
  }

  private trimLiveRenderSource(sourceId: number): void {
    const arr = this.liveRenderData.get(sourceId);
    if (!arr || arr.length === 0) return;
    const lastTs = StreamingStore.pointTimestampMs(arr[arr.length - 1]);
    const ref = lastTs > 0 ? lastTs : Date.now();
    const cutoff = ref - this.getHistoricalRetentionMs();
    let startIdx = 0;
    while (startIdx < arr.length && StreamingStore.pointTimestampMs(arr[startIdx]) < cutoff) {
      startIdx++;
    }
    if (startIdx > 0) {
      arr.splice(0, startIdx);
    }
  }

  private trimLiveTrackSource(sourceId: number): void {
    const arr = this.liveTrackData.get(sourceId);
    if (!arr || arr.length === 0) return;
    const lastTs = StreamingStore.pointTimestampMs(arr[arr.length - 1]);
    const ref = lastTs > 0 ? lastTs : Date.now();
    const cutoff = ref - this.getHistoricalRetentionMs();
    let startIdx = 0;
    while (startIdx < arr.length && StreamingStore.pointTimestampMs(arr[startIdx]) < cutoff) {
      startIdx++;
    }
    if (startIdx > 0) {
      arr.splice(0, startIdx);
    }
  }

  private mergeLiveTrackDataFromRedis(sourceId: number, points: any[]): number {
    if (!points.length) return 0;
    const existing = this.liveTrackData.get(sourceId) ?? [];
    const { merged, added } = StreamingStore.mergeSortedTrackPointsDedupe(existing, points);
    if (added > 0) {
      this.liveTrackData.set(sourceId, merged);
      this.trimLiveTrackSource(sourceId);
    }
    return added;
  }

  private getLastLiveTrackTimestampMs(sourceId: number): number | null {
    const arr = this.liveTrackData.get(sourceId);
    if (!arr?.length) return null;
    const ts = StreamingStore.pointTimestampMs(arr[arr.length - 1]);
    return ts > 0 ? ts : null;
  }

  private normalizeRedisTrackPoints(points: any[]): any[] {
    return points.map((point) => {
      const normalized = { ...point };
      if (normalized.Lat_dd !== undefined && normalized.Lat === undefined) {
        normalized.Lat = normalized.Lat_dd;
      }
      if (normalized.Lng_dd !== undefined && normalized.Lng === undefined) {
        normalized.Lng = normalized.Lng_dd;
      }
      if (normalized.lat_dd !== undefined && normalized.lat === undefined) {
        normalized.lat = normalized.lat_dd;
      }
      if (normalized.lng_dd !== undefined && normalized.lng === undefined) {
        normalized.lng = normalized.lng_dd;
      }
      StreamingStore.syncPointCoordinates(normalized);
      return normalized;
    });
  }

  private logTrackRefreshSkippedBecauseInFlight(): void {
    const now = Date.now();
    if (now - this.lastTrackRefreshSkippedLogAt < 2000) return;
    this.lastTrackRefreshSkippedLogAt = now;
    debug('[StreamingStore] Coalescing live track refresh — previous refresh still in flight', {
      skippedBecauseInFlight: true,
      willRetryAfterInFlight: true,
    });
  }

  private warnTrackRefreshBeforeSeed(sourceId: number): void {
    if (this.trackRefreshBeforeSeedWarned.has(sourceId)) return;
    this.trackRefreshBeforeSeedWarned.add(sourceId);
    debug('[StreamingStore] Skipping tail track refresh until the initial history seed completes', {
      sourceId,
    });
  }

  private seedLiveRenderDataFromRedis(
    sourceId: number,
    points: any[],
    options?: { forceTailFromRedis?: boolean }
  ): void {
    if (!points.length) return;
    const sorted = StreamingStore.sortPointsByTimestamp(points);
    const current = this.liveRenderData.get(sourceId);
    if (!current || current.length === 0) {
      this.liveRenderData.set(sourceId, [...sorted]);
      this.trimLiveRenderSource(sourceId);
      return;
    }

    const firstLiveTs = StreamingStore.pointTimestampMs(current[0]);
    const lastLiveTs = StreamingStore.pointTimestampMs(current[current.length - 1]);
    const prefix = sorted.filter((p) => {
      const ts = StreamingStore.pointTimestampMs(p);
      return ts > 0 && ts < firstLiveTs;
    });

    // Processing mode: always append the history tail (sparse WS). Otherwise skip suffix while WS is hot.
    const wsActive =
      !options?.forceTailFromRedis &&
      this.isWebSocketRecentlyActive(liveConfigStore.wsBroadcastIntervalMs() + 1000);
    const suffix = wsActive
      ? []
      : sorted.filter((p) => {
          const ts = StreamingStore.pointTimestampMs(p);
          return ts > 0 && ts > lastLiveTs;
        });

    if (prefix.length > 0 || suffix.length > 0) {
      this.liveRenderData.set(sourceId, [...prefix, ...current, ...suffix]);
      this.trimLiveRenderSource(sourceId);
    }
  }

  private appendLiveRenderPointFromWebSocket(sourceId: number, point: any): boolean {
    if (!this.liveRenderData.has(sourceId)) {
      this.liveRenderData.set(sourceId, []);
    }
    const arr = this.liveRenderData.get(sourceId)!;
    const ts = StreamingStore.pointTimestampMs(point);
    if (ts <= 0) return false;
    const lastTs = arr.length > 0 ? StreamingStore.pointTimestampMs(arr[arr.length - 1]) : 0;
    if (lastTs > 0 && ts <= lastTs) {
      return false;
    }
    arr.push(point);
    this.trimLiveRenderSource(sourceId);
    return true;
  }

  /** @internal Used by streamingService data_batch handler */
  beginReactiveBatch(): void {
    this.reactiveBatchDepth++;
  }

  /** @internal Used by streamingService data_batch handler */
  endReactiveBatch(): void {
    if (this.reactiveBatchDepth <= 0) return;
    this.reactiveBatchDepth--;
    if (this.reactiveBatchDepth === 0) {
      this.flushReactiveBatch();
    }
  }

  private flushReactiveBatch(): void {
    if (this.batchPendingNewData) {
      this.newDataSignal[1](this.batchPendingNewData);
      this.batchPendingNewData = null;
    }
    if (this.batchPendingLiveAppend) {
      this.liveDataAppendVersionSignal[1]((v) => v + 1);
      this.batchPendingLiveAppend = false;
    }
  }

  private emitLiveDataSignals(sourceId: number, mergedPoint: any): void {
    this.lastWebSocketAppendAt = Date.now();
    if (this.reactiveBatchDepth > 0) {
      if (!this.batchPendingNewData) {
        this.batchPendingNewData = new Map(this.newDataSignal[0]());
      }
      this.batchPendingNewData.set(sourceId, [mergedPoint]);
      this.batchPendingLiveAppend = true;
      return;
    }
    const currentData = this.newDataSignal[0]();
    const updatedData = new Map(currentData);
    updatedData.set(sourceId, [mergedPoint]);
    this.newDataSignal[1](updatedData);
    this.liveDataAppendVersionSignal[1]((v) => v + 1);
  }

  get isInitialized() {
    return this._isInitialized;
  }

  /** Single in-flight connect for subscribe retries (avoids N parallel connects per source). */
  private ensureStreamingConnected(): Promise<boolean> {
    if (streamingService.isConnected()) {
      return Promise.resolve(true);
    }
    if (!this.sharedConnectPromise) {
      this.sharedConnectPromise = streamingService
        .connect()
        .catch(() => false)
        .finally(() => {
          this.sharedConnectPromise = null;
        });
    }
    return this.sharedConnectPromise;
  }

  private async subscribeSourceWithRetry(
    source_name: string,
    maxRetries = 10,
    initialDelay = 100
  ): Promise<boolean> {
    let retries = maxRetries;
    let delay = initialDelay;

    while (retries > 0) {
      if (!streamingService.isConnected()) {
        await this.ensureStreamingConnected();
      }
      if (streamingService.isConnected()) {
        if (streamingService.subscribe(source_name)) {
          debug(`[StreamingStore] ✅ Subscribed to source "${source_name}" (all channels)`);
          return true;
        }
        const subscriptions = streamingService.getSubscriptions();
        if (subscriptions.has(source_name)) {
          debug(`[StreamingStore] Already subscribed to source "${source_name}"`);
          return true;
        }
        debug(
          `[StreamingStore] ⚠️ Subscribe returned false for "${source_name}", retrying in ${delay}ms (${retries} retries left)`,
          {
            isConnected: streamingService.isConnected(),
          }
        );
      } else {
        debug(
          `[StreamingStore] ⏳ WebSocket not connected for "${source_name}", waiting ${delay}ms (${retries} retries left)`,
          { isConnected: streamingService.isConnected() }
        );
      }

      retries--;
      if (retries > 0) {
        await new Promise((resolve) => setTimeout(resolve, delay));
        delay = Math.min(delay * 1.5, 2000);
      }
    }

    const msg = `[StreamingStore] Failed to subscribe to source "${source_name}" after ${maxRetries} retries`;
    warn(msg, {
      source_name,
      isConnected: streamingService.isConnected(),
    });
    logError('[StreamingStore] ❌', msg);
    return false;
  }

  /**
   * Initialize WebSocket connection and set up data callbacks
   */
  async initialize(selectedSourceIds: Set<number>): Promise<void> {
    debug('[StreamingStore] 🚀 initialize() called', {
      sourceIds: Array.from(selectedSourceIds),
      sourceCount: selectedSourceIds.size,
      ENABLE_WEBSOCKETS: config.ENABLE_WEBSOCKETS
    });
    
    // Check if WebSockets are enabled
    if (!config.ENABLE_WEBSOCKETS) {
      const msg = '[StreamingStore] WebSockets are disabled (set VITE_ENABLE_WEBSOCKETS=true or ENABLE_WEBSOCKETS=true), skipping initialization';
      debug(msg);
      warn('[StreamingStore] ⚠️', msg);
      this.loadingSignal[1](false);
      this.errorSignal[1]('WebSockets are disabled');
      this._isInitialized = false;
      this._isInitializing = false;
      return;
    }
    
    // Prevent multiple simultaneous initializations
    if (this._isInitializing) {
      debug('[StreamingStore] Initialization already in progress, waiting...');
      const maxWait = 15000;
      const pollInterval = 100;
      const startTime = Date.now();
      
      while (this._isInitializing && (Date.now() - startTime) < maxWait) {
        await new Promise(resolve => setTimeout(resolve, pollInterval));
      }
      
      if (this._isInitializing) {
        logError('[StreamingStore] Initialization wait timeout');
        throw new Error('Initialization timeout');
      }
      
      if (this._isInitialized) {
        debug('[StreamingStore] Initialization completed while waiting');
        return;
      }
    }
    
    if (this._isInitialized) {
      if (StreamingStore.setsEqual(selectedSourceIds, this.currentSourceIds)) {
        debug('[StreamingStore] Already initialized with same source IDs, skipping');
        return;
      }
      debug('[StreamingStore] Already initialized, updating subscriptions (keeping live data)');
      await this.updateSelectedSources(selectedSourceIds, this.currentSourceIds);
      this.currentSourceIds = new Set(selectedSourceIds);
      return;
    }

    this._isInitializing = true;
    this.loadingSignal[1](true);
    this.errorSignal[1](null);

    try {
      // Connect to WebSocket
      debug('[StreamingStore] Attempting to connect to streaming service...');
      debug('[StreamingStore] 🔌 Attempting to connect to WebSocket...');
      const connected = await streamingService.connect();
      debug('[StreamingStore] 🔌 Connection result:', connected);
      debug('[StreamingStore] Connection result:', connected);
      if (!connected) {
        const errorMsg = streamingService.getLastError();
        debug('[StreamingStore] getLastError() returned:', errorMsg);
        const finalErrorMsg = errorMsg || 'Failed to connect to streaming service (no error details available)';
        logError('[StreamingStore] Connection failed:', finalErrorMsg);
        throw new Error(finalErrorMsg);
      }
      debug('[StreamingStore] Successfully connected to streaming service');
      debug('[StreamingStore] ✅ Successfully connected to streaming service');

      streamingService.setDataBatchHooks(
        () => this.beginReactiveBatch(),
        () => this.endReactiveBatch()
      );

      // Subscribe to data updates - feed into unifiedDataStore
      const unsubscribeData = streamingService.onData((dataPoint: StreamingDataPoint) => {
        // Handle asynchronously to avoid blocking
        this.handleWebSocketData(dataPoint).catch(err => {
          logError('[StreamingStore] Error in handleWebSocketData:', err);
        });
      });

      // Subscribe to connection changes
      const unsubscribeConnection = streamingService.onConnection((connected) => {
        if (!connected) {
          this.errorSignal[1]('Disconnected from streaming service');
        } else {
          this.errorSignal[1](null);
          // Re-subscribe to all sources with a small delay to ensure WebSocket is ready
          // Use queueMicrotask to ensure this happens after the connection is fully established
          queueMicrotask(async () => {
            const subscriptionPromises: Promise<boolean>[] = [];
            for (const source_id of selectedSourceIds) {
              const source_name = sourcesStore.getSourceName(source_id);
              if (source_name) {
                subscriptionPromises.push(this.subscribeSourceWithRetry(source_name));
              } else {
                warn(`[StreamingStore] Could not get source_name for source_id ${source_id}, skipping subscription`);
              }
            }
            await Promise.all(subscriptionPromises);
            debug('[StreamingStore] ✅ All re-subscription attempts completed');
          });
        }
      });

      this.unsubscribeCallbacks.push(unsubscribeData, unsubscribeConnection);

      debug('[StreamingStore] 📡 Setting up subscriptions for', selectedSourceIds.size, 'sources');

      queueMicrotask(async () => {
        const subscriptionPromises: Promise<boolean>[] = [];
        const availableSources = sourcesStore.sources();
        debug('[StreamingStore] Available sources for subscription:', {
          sourceCount: availableSources.length,
          sources: availableSources.map(s => ({ source_id: s.source_id, source_name: s.source_name })),
          selectedSourceIds: Array.from(selectedSourceIds),
          sourcesStoreReady: sourcesStore.isReady()
        });

        for (const source_id of selectedSourceIds) {
          const source_name = sourcesStore.getSourceName(source_id);
          if (source_name) {
            debug(`[StreamingStore] 📡 Attempting to subscribe to source_id ${source_id} (source_name: "${source_name}")`);
            subscriptionPromises.push(this.subscribeSourceWithRetry(source_name));
          } else {
            const msg = `[StreamingStore] Could not get source_name for source_id ${source_id}, skipping subscription`;
            warn(msg, {
              source_id,
              sourcesStoreReady: sourcesStore.isReady(),
              availableSourceIds: availableSources.map(s => s.source_id),
              availableSourceNames: availableSources.map(s => s.source_name)
            });
            warn('[StreamingStore] ⚠️', msg);
          }
        }

        const results = await Promise.all(subscriptionPromises);
        const subscribedCount = results.filter(Boolean).length;
        const hasLiveSubscriptions = streamingService.getSubscriptions().size > 0;

        this.loadingSignal[1](false);
        this._isInitializing = false;
        this.currentSourceIds = new Set(selectedSourceIds);

        if (subscribedCount > 0 || hasLiveSubscriptions) {
          this._isInitialized = true;
          this.errorSignal[1](null);
          debug('[StreamingStore] ✅ Initialized successfully', {
            subscribedCount,
            subscriptionTotal: streamingService.getSubscriptions().size
          });
        } else {
          this._isInitialized = false;
          const errMsg = 'Failed to subscribe to any live streaming sources (WebSocket unavailable?)';
          this.errorSignal[1](errMsg);
          warn('[StreamingStore] ⚠️', errMsg);
          this.streamingLifecycleSignal[1]((v) => v + 1);
        }
      });
    } catch (err) {
      logError('[StreamingStore] Initialization failed:', err);
      this.errorSignal[1](err instanceof Error ? err.message : 'Initialization failed');
      this.loadingSignal[1](false);
      this._isInitializing = false;
      this._isInitialized = false;
    }
  }

  /**
   * Handle WebSocket data updates - emit immediately (no batching)
   */
  private async handleWebSocketData(dataPoint: StreamingDataPoint): Promise<void> {
    try {
      // Simulation replay: the history gap-backfill (the designated fast lane) already feeds every
      // consumer — boats (liveRenderData frame points), tracks (liveTrackData), chart + overlay
      // (historicalData at the playhead). The WebSocket lane here would redo the same per-point
      // merge/insert/track work (~19% of frame time in profiling) only to have most points rejected
      // as timestamp duplicates, and it bumps the track version at WS cadence (~200ms) causing extra
      // full track redraws. Since replay runs behind wall clock, we don't need WS's lower latency —
      // skip it entirely in simulation and let the 1s backfill own the catalogs.
      if (liveConfigStore.activeCadence() === 'simulation') {
        return;
      }

      // Extract source_name from data
      const sourceName = dataPoint.source_name || dataPoint.data?.source_name;
      if (!sourceName) {
        debug('[StreamingStore] WebSocket data point missing source_name, skipping');
        return;
      }

      // Convert WebSocket data point to format expected by unifiedDataStore
      // The data object contains all channel values
      const channels = Object.keys(dataPoint.data || {});
      if (channels.length === 0) {
        debug('[StreamingStore] WebSocket data point has no channels, skipping');
        return;
      }

      // Get source_id for signal emission
      // Prefer sourcesStore lookup (most reliable), but fallback to server's source_id if sourcesStore isn't ready
      let sourceId: number | null = null;
      
      // Try to get correct source_id from sourcesStore first
      if (sourcesStore.isReady()) {
        const correctSourceId = sourcesStore.getSourceId(sourceName);
        if (correctSourceId !== null) {
          sourceId = correctSourceId;
          
          // Validate that server's source_id matches the correct one (if provided)
          // Only warn once per source to avoid log spam
          const serverSourceId = dataPoint.source_id ?? null;
          if (serverSourceId !== null && serverSourceId !== correctSourceId) {
            if (!this.sourceIdMismatchWarned.has(sourceName)) {
              debug('[StreamingStore] Server source_id mismatch - using correct source_id from sourcesStore', {
                source_name: sourceName,
                server_source_id: serverSourceId,
                correct_source_id: correctSourceId,
                note: 'Server sent incorrect source_id; using correct mapping from sourcesStore. Logged once per source.'
              });
              this.sourceIdMismatchWarned.add(sourceName);
            }
          }
        }
      }
      
      // Fallback: use server's source_id if sourcesStore lookup failed or isn't ready
      if (sourceId === null) {
        const serverSourceId = dataPoint.source_id ?? null;
        if (serverSourceId !== null) {
          sourceId = serverSourceId;
          if (!sourcesStore.isReady()) {
            debug('[StreamingStore] sourcesStore not ready, using server source_id temporarily', {
              source_name: sourceName,
              server_source_id: serverSourceId
            });
          } else if (!this.sourceIdLookupWarned.has(sourceName)) {
            this.sourceIdLookupWarned.add(sourceName);
            warn('[StreamingStore] Could not find source in sourcesStore, using server source_id', {
              source_name: sourceName,
              server_source_id: serverSourceId,
              availableSources: sourcesStore.sources().map(s => ({ source_id: s.source_id, source_name: s.source_name }))
            });
          }
        }
      }
      
      // Final check - if we still don't have a source_id, we can't process this data
      if (sourceId === null) {
        if (!this.sourceIdMissingWarned.has(sourceName)) {
          this.sourceIdMissingWarned.add(sourceName);
          warn('[StreamingStore] Cannot determine source_id for data point', {
            source_name: sourceName,
            dataPoint_source_id: dataPoint.source_id,
            sourcesStoreReady: sourcesStore.isReady(),
            availableSources: sourcesStore.sources().map(s => ({ source_id: s.source_id, source_name: s.source_name }))
          });
        }
        // Cannot process without source_id - return early
        return;
      }
      
      // Get previous point for this source so we can carry forward missing channels (no flashing)
      const currentData = this.newDataSignal[0]();
      const previousPoints = currentData.get(sourceId);
      const previousPoint = previousPoints?.[0] && typeof previousPoints[0] === 'object' ? previousPoints[0] as Record<string, unknown> : null;
      const metaKeys = new Set(['timestamp', 'source_name', 'source_id', 'Datetime', 'datetime']);
      /** Do not carry these from the previous WS point — they are tied to the current sample; partial payloads would show stale values (e.g. BSP 1 with GRADE 3). */
      const carryForwardBlocklist = new Set<string>(['GRADE']);
      const incomingHasGps = StreamingStore.incomingPayloadHasGps(
        dataPoint.data as Record<string, unknown> | undefined
      );
      if (incomingHasGps) {
        for (const gpsKey of StreamingStore.GPS_CHANNEL_NAMES) {
          carryForwardBlocklist.add(gpsKey);
        }
      }

      // Create a merged data point: new values override, missing channels keep previous value
      const mergedPoint: any = {
        timestamp: dataPoint.timestamp,
        source_name: sourceName,
        source_id: sourceId
      };

      // Copy from incoming data only when the value is numeric.
      // Empty stream values should not clear the current live overlay value.
      for (const [channel, value] of Object.entries(dataPoint.data || {})) {
        const numericValue = StreamingStore.numericLiveChannelValue(channel, value);
        if (!metaKeys.has(channel) && numericValue !== undefined) {
          mergedPoint[channel] = numericValue;
        }
      }
      // Carry forward from previous point for channels not in this update (smooth display, no empty flash)
      if (previousPoint) {
        for (const [channel, value] of Object.entries(previousPoint)) {
          if (carryForwardBlocklist.has(channel)) continue;
          const numericValue = StreamingStore.numericLiveChannelValue(channel, value);
          if (!metaKeys.has(channel) && mergedPoint[channel] === undefined && numericValue !== undefined) {
            mergedPoint[channel] = numericValue;
          }
        }
      }

      // Ensure Datetime exists for bad air overlay (use from data if available, otherwise from timestamp)
      // Bad air checks: d.Datetime ?? d.datetime
      // The server sends Datetime as an ISO string (getter property), so we need to handle it
      if (!mergedPoint.Datetime && !mergedPoint.datetime) {
        // Create Datetime from timestamp if not provided in data
        mergedPoint.Datetime = new Date(dataPoint.timestamp);
      } else if (mergedPoint.datetime && !mergedPoint.Datetime) {
        // If only lowercase datetime exists, also set uppercase Datetime
        mergedPoint.Datetime = mergedPoint.datetime instanceof Date 
          ? mergedPoint.datetime 
          : new Date(mergedPoint.datetime);
      } else if (mergedPoint.Datetime) {
        // Datetime exists - ensure it's a Date object (server sends as ISO string)
        if (typeof mergedPoint.Datetime === 'string') {
          mergedPoint.Datetime = new Date(mergedPoint.Datetime);
        } else if (!(mergedPoint.Datetime instanceof Date)) {
          // Convert to Date if it's not already
          mergedPoint.Datetime = new Date(mergedPoint.Datetime);
        }
        // Also set lowercase datetime for compatibility (bad air checks both)
        if (!mergedPoint.datetime) {
          mergedPoint.datetime = mergedPoint.Datetime;
        }
      }

      // Normalize coordinates: prefer Lat_dd over stale Lat/Lng after partial WS merge
      StreamingStore.syncPointCoordinates(mergedPoint);

      // Latest point + history: skip reactive updates when this timestamp already exists (duplicate resend).
      // Previously newDataSignal fired on every WS message, which re-ran map effects even when history rejected the point.
      if (sourceId !== null && sourceId !== undefined) {
        if (!this.historicalData.has(sourceId)) {
          this.historicalData.set(sourceId, []);
        }
        const historicalPoints = this.historicalData.get(sourceId)!;

        const inserted = this.insertHistoricalPoint(historicalPoints, mergedPoint);
        if (inserted) {
          this.trimHistoricalSource(sourceId);
          this.appendLiveRenderPointFromWebSocket(sourceId, mergedPoint);
          if (StreamingStore.resolvePointCoordinates(mergedPoint) != null) {
            const trackAdded = this.mergeLiveTrackDataFromRedis(sourceId, [mergedPoint]);
            if (trackAdded > 0) {
              this.bumpTrackDataVersion();
            }
          }

          this.dataPointCount++;
          this.emitLiveDataSignals(sourceId, mergedPoint);
        }
      }
    } catch (err) {
      // Do not rethrow: one malformed packet must not kill the live feed. But a bug on this path
      // (a TypeError, a renamed dependency) fails identically for EVERY packet, and a bare log
      // makes that indistinguishable from "no data is arriving" - which is exactly how a missing
      // liveConfigStore.activeCadence silently emptied every WebSocket test. So say which it is:
      // log the first error with context, then stay quiet unless nothing is getting through, in
      // which case report it as a systematic drop rather than an idle feed.
      this.wsHandlingErrorCount++;
      const nothingAccepted = this.dataPointCount === 0;
      const isFirst = this.wsHandlingErrorCount === 1;

      if (isFirst || (nothingAccepted && this.wsHandlingErrorCount % 50 === 0)) {
        logError('[StreamingStore] Error handling WebSocket data:', err, {
          source_name: dataPoint?.source_name,
          errors: this.wsHandlingErrorCount,
          points_accepted: this.dataPointCount,
          note: nothingAccepted && !isFirst
            ? 'Every WebSocket point so far has failed - the live lane is dropping all data, not '
              + 'sitting idle. Suspect this handler or one of its dependencies, not the feed.'
            : 'Further errors are counted, not logged per point; see getWebSocketHandlingErrorCount().'
        });
      }
    }
  }

  /**
   * WebSocket points dropped by handleWebSocketData's catch.
   *
   * Read this alongside the accepted-point count when the live view looks empty: errors climbing
   * while nothing is accepted means the handler is broken, not that the feed is quiet.
   */
  getWebSocketHandlingErrorCount(): number {
    return this.wsHandlingErrorCount;
  }

  /**
   * Get reactive signal for new data updates (immediate, no batching)
   * Components can watch this to receive updates as soon as websocket data arrives
   */
  getNewData() {
    return this.newDataSignal[0];
  }

  /**
   * Reactive signal for when historical data is loaded from the history endpoint.
   * Components (e.g. LiveMapTimeSeries) can watch this to re-render when that history arrives.
   */
  getHistoricalDataVersion() {
    return this.historicalDataVersionSignal[0];
  }

  /**
   * Reactive signal bumped on every WebSocket append to historicalData.
   * Use this so chart/map re-run when new points arrive even after getNewData() is cleared.
   */
  getLiveDataAppendVersion() {
    return this.liveDataAppendVersionSignal[0];
  }

  /** Reactive: live map track layer — history/store slow lane only. */
  getTrackDataVersion() {
    return this.trackDataVersionSignal[0];
  }

  private bumpTrackDataVersion(): void {
    this.trackDataVersionSignal[1]((v) => v + 1);
  }

  /** Reactive: subscribe in effects that must re-connect after streamingStore.cleanup(). */
  getStreamingLifecycleEpoch() {
    return this.streamingLifecycleSignal[0];
  }

  /**
   * Clear processed data from the signal for specified sources
   * Optionally clear only points up to a certain timestamp to preserve newer data
   */
  clearProcessedData(sourceIds: Set<number>, upToTimestamp?: Map<number, number>): void {
    const currentData = this.newDataSignal[0]();
    const updatedData = new Map(currentData);
    
    let hasChanges = false;
    for (const sourceId of sourceIds) {
      const points = updatedData.get(sourceId);
      if (!points || points.length === 0) {
        continue;
      }

      if (upToTimestamp && upToTimestamp.has(sourceId)) {
        // Clear only points up to the specified timestamp, keep newer ones
        const maxTimestamp = upToTimestamp.get(sourceId)!;
        const getTimestamp = (d: any): number => {
          if (!d) return 0;
          const ts = d.timestamp || (d.Datetime instanceof Date ? d.Datetime.getTime() : new Date(d.Datetime).getTime());
          return Number.isFinite(ts) ? ts : 0;
        };
        
        const remainingPoints = points.filter(p => getTimestamp(p) > maxTimestamp);
        
        if (remainingPoints.length === 0) {
          // All points were processed, remove the entry
          updatedData.delete(sourceId);
        } else {
          // Keep newer points
          updatedData.set(sourceId, remainingPoints);
        }
        hasChanges = true;
      } else {
        // No timestamp specified, clear all data for this source
        updatedData.delete(sourceId);
        hasChanges = true;
      }
    }
    
    if (hasChanges) {
      this.newDataSignal[1](updatedData);
      debug('[StreamingStore] Cleared processed data for sources', {
        sourceIds: Array.from(sourceIds),
        remainingSources: Array.from(updatedData.keys()),
        usedTimestampFilter: !!upToTimestamp
      });
    }
  }

  /**
   * Update selected sources (subscribe/unsubscribe from WebSocket)
   */
  async updateSelectedSources(selectedSourceIds: Set<number>, previousSourceIds: Set<number>): Promise<void> {
    if (!this._isInitialized) {
      debug('[StreamingStore] Not initialized, cannot update sources');
      return;
    }

    // Unsubscribe from removed sources
    for (const source_id of previousSourceIds) {
      if (!selectedSourceIds.has(source_id)) {
        const source_name = sourcesStore.getSourceName(source_id);
        if (source_name) {
          streamingService.unsubscribe(source_name);
          debug(`[StreamingStore] Unsubscribed from source "${source_name}" (source_id: ${source_id})`);
        } else {
          warn(`[StreamingStore] Could not get source_name for source_id ${source_id} to unsubscribe`);
        }
      }
    }

    // Subscribe to new sources (no channel filter = get all processor-mapped data)
    for (const source_id of selectedSourceIds) {
      if (!previousSourceIds.has(source_id)) {
        const source_name = sourcesStore.getSourceName(source_id);
        if (source_name) {
          streamingService.subscribe(source_name);
          debug(`[StreamingStore] Subscribed to source "${source_name}" (source_id: ${source_id})`);
        } else {
          warn(`[StreamingStore] Could not get source_name for source_id ${source_id} to subscribe`);
        }
      }
    }
  }

  /**
   * Get reactive loading signal
   */
  loading() {
    return this.loadingSignal[0];
  }

  /**
   * Get reactive error signal
   */
  error() {
    return this.errorSignal[0];
  }

  /**
   * Stable key so rapid catch-up calls (same sources + window, end time within same second) share one load.
   */
  private redisLoadCoalesceKey(sourceIds: Set<number>, minutes: number, endTimeMs: number): string {
    const ids = Array.from(sourceIds)
      .sort((a, b) => a - b)
      .join(',');
    const endBucket = Math.floor(endTimeMs / 1000) * 1000;
    return `${ids}|${minutes}|${endBucket}`;
  }

  /**
   * Load initial historical data over REST for specified sources
   * @param sourceIds Set of source IDs to load data for
   * @param minutes Number of minutes of historical data to load
   * @param endTime Optional end time in milliseconds (defaults to Date.now())
   */
  async loadInitialDataFromRedis(sourceIds: Set<number>, minutes: number, endTime?: number): Promise<void> {
    if (sourceIds.size === 0) {
      debug('[StreamingStore] ⚠️ No sources to load, skipping loadInitialDataFromRedis');
      return;
    }

    const effectiveEndTime = endTime ?? Date.now();
    const coalesceKey = this.redisLoadCoalesceKey(sourceIds, minutes, effectiveEndTime);
    const inflight = this.redisLoadInflight.get(coalesceKey);
    if (inflight) {
      debug('[StreamingStore] Coalescing loadInitialDataFromRedis onto in-flight request', { coalesceKey });
      return inflight;
    }

    const run = this.loadInitialDataFromRedisImpl(sourceIds, minutes, endTime);
    this.redisLoadInflight.set(coalesceKey, run);
    try {
      await run;
    } finally {
      this.redisLoadInflight.delete(coalesceKey);
    }
  }

  private async loadInitialDataFromRedisImpl(sourceIds: Set<number>, minutes: number, endTime?: number): Promise<void> {
    try {
      // Use provided endTime or fallback to current time
      const effectiveEndTime = endTime ?? Date.now();
      
      debug('[StreamingStore] 🔄 Loading initial history', {
        sourceCount: sourceIds.size,
        minutes,
        endTime: effectiveEndTime,
        endTimeProvided: endTime !== undefined,
        sourceIds: Array.from(sourceIds),
        sourceNames: Array.from(sourceIds).map(id => sourcesStore.getSourceName(id))
      });

      // For live map, load ALL available history available (up to 24 hours)
      // This ensures the timeseries shows complete history, then fills gaps to latest
      // Use a large window (24 hours) to get complete history, or use minutes if specified
      const windowMinutes = minutes > 0 ? minutes : 24 * 60; // Default to 24 hours if minutes is 0
      const startTime = effectiveEndTime - (windowMinutes * 60 * 1000);

      // Processor outputs default channel names (Lat_dd, Bsp_kph, GRADE, etc.)
      const channels = [
        ...LIVE_STREAM_CHANNELS,
        'Datetime' // Timestamp
      ];
      
      debug('[StreamingStore] Requesting default channel names from the stream server', {
        channels,
        note: 'Processor now stores default channel names with units'
      });

      // Fetch data for all sources in parallel
      const sourceNames = Array.from(sourceIds).map(id => {
        const name = sourcesStore.getSourceName(id);
        if (!name) {
          warn(`[StreamingStore] Could not get source_name for source_id ${id}`);
        }
        return { sourceId: id, sourceName: name };
      }).filter(s => s.sourceName);

      const fetchPromises = sourceNames.map(async ({ sourceId, sourceName }) => {
        try {
          const mergedData = await streamingDataService.fetchMergedData(
            sourceName!,
            channels,
            startTime,
            effectiveEndTime
          );

          // Store in historical data
          if (mergedData.length > 0) {
            // Normalize coordinates: ensure both Lat/Lng and Lat_dd/Lng_dd are available
            // This ensures components can use either naming convention
            const normalizedData = mergedData.map(point => {
              const normalized = { ...point };
              
              // If we have Lat_dd/Lng_dd but not Lat/Lng, add normalized names
              if (normalized.Lat_dd !== undefined && normalized.Lat === undefined) {
                normalized.Lat = normalized.Lat_dd;
              }
              if (normalized.Lng_dd !== undefined && normalized.Lng === undefined) {
                normalized.Lng = normalized.Lng_dd;
              }
              
              // Also ensure lowercase variants for compatibility
              if (normalized.lat_dd !== undefined && normalized.lat === undefined) {
                normalized.lat = normalized.lat_dd;
              }
              if (normalized.lng_dd !== undefined && normalized.lng === undefined) {
                normalized.lng = normalized.lng_dd;
              }

              StreamingStore.syncPointCoordinates(normalized);
              return normalized;
            });
            
            const dedupedTrackData = dedupeTrackPointsByTimestamp(normalizedData);
            this.historicalData.set(
              sourceId,
              StreamingStore.sortPointsByTimestamp(normalizedData)
            );
            this.trimHistoricalSource(sourceId);
            this.seedLiveRenderDataFromRedis(sourceId, normalizedData);
            this.liveTrackData.set(sourceId, dedupedTrackData);
            this.trimLiveTrackSource(sourceId);
            // Debug: Check if first point has coordinates (using default channel names)
            const firstPoint = normalizedData[0];
            const hasLat = !!(firstPoint.Lat ?? firstPoint.Lat_dd ?? firstPoint.lat ?? firstPoint.lat_dd);
            const hasLng = !!(firstPoint.Lng ?? firstPoint.Lng_dd ?? firstPoint.lng ?? firstPoint.lng_dd);
            debug(`[StreamingStore] ✅ Loaded ${normalizedData.length} points for source ${sourceId} (${sourceName})`, {
              firstPointKeys: Object.keys(firstPoint).slice(0, 15),
              hasLat,
              hasLng,
              latValue: firstPoint.Lat ?? firstPoint.Lat_dd ?? firstPoint.lat ?? firstPoint.lat_dd,
              lngValue: firstPoint.Lng ?? firstPoint.Lng_dd ?? firstPoint.lng ?? firstPoint.lng_dd,
              allChannels: Object.keys(firstPoint)
            });
          } else {
            // Do NOT overwrite with [] - WebSocket may have already pushed data; leave existing data intact
            if (!this.historicalData.has(sourceId)) {
              this.historicalData.set(sourceId, []);
            }
            debug(`[StreamingStore] ⚠️ No data found for source ${sourceId} (${sourceName})`, {
              requestedChannels: channels,
              note: 'Check if channels exist with default channel names'
            });
          }
        } catch (err) {
          // Network errors on the history endpoint are expected - log as warning, not error
          warn(`[StreamingStore] Network error loading data for source ${sourceId} (expected when the history endpoint is unavailable)`);
          // Do NOT overwrite existing data (e.g. from WebSocket) - only set empty if source had no key
          if (!this.historicalData.has(sourceId)) {
            this.historicalData.set(sourceId, []);
          }
        }
      });

      await Promise.all(fetchPromises);

      const totalPoints = Array.from(this.historicalData.values()).reduce((sum, arr) => sum + arr.length, 0);
      debug('[StreamingStore] ✅ Initial history loaded', {
        sourceCount: sourceIds.size,
        totalPoints,
        pointsPerSource: Array.from(this.historicalData.entries()).map(([id, data]) => ({
          sourceId: id,
          sourceName: sourcesStore.getSourceName(id),
          pointCount: data.length
        }))
      });

      // Bump signal so timeline (groupedData) re-renders with loaded data
      if (totalPoints > 0) {
        this.historicalDataVersionSignal[1](v => v + 1);
        for (const sourceId of sourceIds) {
          const hist = this.historicalData.get(sourceId);
          if (hist?.length) {
            this.seedLiveRenderDataFromRedis(sourceId, hist);
          }
        }
        this.bumpTrackDataVersion();
      }
    } catch (err) {
      // Network errors on the history endpoint are expected - log as warning, not error
      warn('[StreamingStore] Network error loading initial history (expected when the history endpoint is unavailable)');
    }
  }

  /**
   * Pull a time window from the history endpoint around the current playback position and merge into historical data.
   * Fills gaps when WebSocket pushes are missed so playback stays smooth.
   * @param sourceIds Selected source IDs to fetch
   * @param centerTimeMs Playback position (e.g. selectedTime) in ms
   * @param beforeMs Window before center (default 10000 = 10s)
   * @param afterMs Window after center (default 2000 = 2s)
   */
  async pullPlaybackWindowFromRedis(
    sourceIds: Set<number>,
    centerTimeMs: number,
    beforeMs: number = 10000,
    afterMs: number = 2000,
    options?: { force?: boolean; bumpHistoricalVersion?: boolean }
  ): Promise<void> {
    if (sourceIds.size === 0) return;

    const force = options?.force === true;
    const bumpHistorical = options?.bumpHistoricalVersion !== false;

    // WebSocket batches already keep history fresh — skip the history pull while WS is active
    const wsSkipMs = liveConfigStore.wsBroadcastIntervalMs() + 1000;
    if (!force && this.isWebSocketRecentlyActive(wsSkipMs)) {
      return;
    }

    const startTime = centerTimeMs - beforeMs;
    const endTime = centerTimeMs + afterMs;
    const channels = [
      ...LIVE_STREAM_CHANNELS,
      'Datetime'
    ];

    const getTimestamp = (d: any): number => {
      if (!d) return 0;
      const ts = d.timestamp || (d.Datetime instanceof Date ? d.Datetime.getTime() : new Date(d.Datetime).getTime());
      return Number.isFinite(ts) ? ts : 0;
    };

    try {
      const sourceNames = Array.from(sourceIds)
        .map(id => ({ sourceId: id, sourceName: sourcesStore.getSourceName(id) }))
        .filter((s): s is { sourceId: number; sourceName: string } => !!s.sourceName);

      let addedCount = 0;
      for (const { sourceId, sourceName } of sourceNames) {
        try {
          const mergedData = await streamingDataService.fetchMergedData(
            sourceName,
            channels,
            startTime,
            endTime
          );
          if (mergedData.length === 0) continue;

          const normalizedData = mergedData.map(point => {
            const normalized = { ...point };
            if (normalized.Lat_dd !== undefined && normalized.Lat === undefined) normalized.Lat = normalized.Lat_dd;
            if (normalized.Lng_dd !== undefined && normalized.Lng === undefined) normalized.Lng = normalized.Lng_dd;
            if (normalized.lat_dd !== undefined && normalized.lat === undefined) normalized.lat = normalized.lat_dd;
            if (normalized.lng_dd !== undefined && normalized.lng === undefined) normalized.lng = normalized.lng_dd;
            StreamingStore.syncPointCoordinates(normalized);
            return normalized;
          });

          if (!this.historicalData.has(sourceId)) {
            this.historicalData.set(sourceId, []);
          }
          const existing = this.historicalData.get(sourceId)!;
          let sourceAdded = 0;
          for (const p of normalizedData) {
            if (this.insertHistoricalPoint(existing, p)) {
              sourceAdded++;
              addedCount++;
            }
          }
          const trackAdded = this.mergeLiveTrackDataFromRedis(sourceId, normalizedData);
          if (trackAdded > 0) {
            this.bumpTrackDataVersion();
          }
          if (sourceAdded > 0) {
            this.trimHistoricalSource(sourceId);
            const hist = this.historicalData.get(sourceId);
            if (hist?.length) {
              this.seedLiveRenderDataFromRedis(sourceId, hist);
            }
          }
        } catch (err) {
          warn(`[StreamingStore] Pull playback window failed for source ${sourceId}`, err);
        }
      }

      // Merge the newest history point into "latest" per source so live table sees all channels (WebSocket may only send 3)
      const metaKeys = new Set(['timestamp', 'Datetime', 'datetime', 'source_name', 'source_id']);
      const currentData = this.newDataSignal[0]();
      let latestMerged = false;
      const updatedData = new Map(currentData);
      for (const { sourceId } of sourceNames) {
        const hist = this.historicalData.get(sourceId);
        if (!hist || hist.length === 0) continue;
        const newestFromRedis = hist[hist.length - 1];
        if (!newestFromRedis || typeof newestFromRedis !== 'object') continue;
        const currentLatest = currentData.get(sourceId);
        const currentPoint = currentLatest?.[0];
        const merged: Record<string, unknown> = currentPoint && typeof currentPoint === 'object'
          ? { ...currentPoint }
          : {
              timestamp: getTimestamp(newestFromRedis),
              source_name: newestFromRedis.source_name,
              source_id: sourceId
            };
        for (const [key, val] of Object.entries(newestFromRedis)) {
          if (metaKeys.has(key)) continue;
          const numericValue = StreamingStore.numericChannelValue(val);
          if ((merged[key] === undefined || merged[key] === null) && numericValue !== undefined) {
            merged[key] = numericValue;
            latestMerged = true;
          }
        }
        updatedData.set(sourceId, [merged]);
      }
      if (latestMerged) {
        this.newDataSignal[1](updatedData);
      }

      if (addedCount > 0 && bumpHistorical) {
        this.historicalDataVersionSignal[1](v => v + 1);
      }
    } catch (err) {
      warn('[StreamingStore] Error pulling playback window from the history endpoint', err);
    }
  }

  private getLastLocalCatalogTimestampMs(sourceId: number): number | null {
    const fromHist = this.historicalData.get(sourceId);
    const fromRender = this.liveRenderData.get(sourceId);
    let latest: number | null = null;
    if (fromHist?.length) {
      const ts = StreamingStore.pointTimestampMs(fromHist[fromHist.length - 1]);
      if (ts > 0) latest = ts;
    }
    if (fromRender?.length) {
      const ts = StreamingStore.pointTimestampMs(fromRender[fromRender.length - 1]);
      if (ts > 0 && (latest === null || ts > latest)) latest = ts;
    }
    return latest;
  }

  private mergePendingLiveBoatBackfill(
    sourceIds: Set<number>,
    queryEndMs: number
  ): { sourceIds: Set<number>; queryEndMs: number } {
    const pending = this.pendingLiveBoatBackfill;
    if (!pending) {
      return { sourceIds, queryEndMs };
    }
    const mergedIds = new Set(pending.sourceIds);
    for (const id of sourceIds) mergedIds.add(id);
    return {
      sourceIds: mergedIds,
      queryEndMs: Math.max(pending.queryEndMs, queryEndMs),
    };
  }

  private clearLiveBoatBackfillDeferredTimer(): void {
    if (this.liveBoatBackfillDeferredTimer != null) {
      clearTimeout(this.liveBoatBackfillDeferredTimer);
      this.liveBoatBackfillDeferredTimer = null;
    }
  }

  private scheduleDeferredLiveBoatBackfill(delayMs: number): void {
    this.clearLiveBoatBackfillDeferredTimer();
    if (!this.pendingLiveBoatBackfill) return;
    const waitMs = Math.max(0, delayMs);
    this.liveBoatBackfillDeferredTimer = setTimeout(() => {
      this.liveBoatBackfillDeferredTimer = null;
      const pending = this.pendingLiveBoatBackfill;
      this.pendingLiveBoatBackfill = null;
      if (pending) {
        void this.backfillLiveMapGapFromRedis(pending.sourceIds, pending.queryEndMs);
      }
    }, waitMs);
  }

  /**
   * Processing mode: pull history from the last local catalog point through queryEndMs (boats + tracks).
   */
  async backfillLiveMapGapFromRedis(
    sourceIds: Set<number>,
    queryEndMs: number = Date.now()
  ): Promise<void> {
    if (sourceIds.size === 0) return;

    const cadence = liveConfigStore.activeCadence();
    const wsSkipMs = liveConfigStore.wsBroadcastIntervalMs() + 1000;
    if (
      (cadence === 'streaming' || cadence === 'simulation') &&
      this.isWebSocketRecentlyActive(wsSkipMs)
    ) {
      return;
    }

    const minIntervalMs = liveConfigStore.trackRefreshIntervalMs();
    const sinceLastMs = Date.now() - this.lastLiveMapBackfillCompletedAt;
    if (!this.liveBoatBackfillInFlight && this.lastLiveMapBackfillCompletedAt > 0 && sinceLastMs < minIntervalMs) {
      this.pendingLiveBoatBackfill = this.mergePendingLiveBoatBackfill(sourceIds, queryEndMs);
      this.scheduleDeferredLiveBoatBackfill(minIntervalMs - sinceLastMs);
      return;
    }

    if (this.liveBoatBackfillInFlight) {
      this.pendingLiveBoatBackfill = this.mergePendingLiveBoatBackfill(sourceIds, queryEndMs);
      return;
    }

    this.clearLiveBoatBackfillDeferredTimer();

    const run = this.backfillLiveMapGapImpl(sourceIds, queryEndMs);
    this.liveBoatBackfillInFlight = run;
    try {
      await run;
    } finally {
      this.liveBoatBackfillInFlight = null;
      this.lastLiveMapBackfillCompletedAt = Date.now();
      const pending = this.pendingLiveBoatBackfill;
      this.pendingLiveBoatBackfill = null;
      if (pending) {
        const elapsed = Date.now() - this.lastLiveMapBackfillCompletedAt;
        const delayMs = Math.max(0, minIntervalMs - elapsed);
        if (delayMs === 0) {
          void this.backfillLiveMapGapFromRedis(pending.sourceIds, pending.queryEndMs);
        } else {
          this.pendingLiveBoatBackfill = pending;
          this.scheduleDeferredLiveBoatBackfill(delayMs);
        }
      }
    }
  }

  private async backfillLiveMapGapImpl(
    sourceIds: Set<number>,
    queryEndMs: number
  ): Promise<void> {
    const endMs = Number.isFinite(queryEndMs) ? queryEndMs : Date.now();
    const pollMs = liveConfigStore.pollIntervalMs();
    const defaultLookbackMs = Math.max(pollMs, 10_000);
    const overlapMs = 500;
    const channels = [...LIVE_STREAM_CHANNELS, 'Datetime'];

    const sourceNames = Array.from(sourceIds)
      .map((sourceId) => ({ sourceId, sourceName: sourcesStore.getSourceName(sourceId) }))
      .filter((entry): entry is { sourceId: number; sourceName: string } => !!entry.sourceName);

    let historicalAdded = 0;
    let trackAdded = 0;
    let anyRedisPoints = false;

    for (const { sourceId, sourceName } of sourceNames) {
      const lastLocalTs = this.getLastLocalCatalogTimestampMs(sourceId);
      const startMs =
        lastLocalTs != null
          ? Math.max(0, lastLocalTs - overlapMs)
          : Math.max(0, endMs - defaultLookbackMs);

      if (startMs >= endMs) continue;

      try {
        const mergedData = await streamingDataService.fetchMergedData(
          sourceName,
          channels,
          startMs,
          endMs
        );
        if (mergedData.length === 0) continue;
        anyRedisPoints = true;

        const normalizedData = this.normalizeRedisTrackPoints(mergedData);

        if (!this.historicalData.has(sourceId)) {
          this.historicalData.set(sourceId, []);
        }
        const historicalPoints = this.historicalData.get(sourceId)!;
        let sourceHistoricalAdded = 0;
        for (const point of normalizedData) {
          if (this.insertHistoricalPoint(historicalPoints, point)) {
            sourceHistoricalAdded++;
            historicalAdded++;
          }
        }
        if (sourceHistoricalAdded > 0) {
          this.trimHistoricalSource(sourceId);
        }

        this.seedLiveRenderDataFromRedis(sourceId, normalizedData, {
          forceTailFromRedis:
            liveConfigStore.activeCadence() === 'processing' ||
            !this.isWebSocketRecentlyActive(liveConfigStore.wsBroadcastIntervalMs() + 1000),
        });

        const trackMergeAdded = this.mergeLiveTrackDataFromRedis(sourceId, normalizedData);
        trackAdded += trackMergeAdded;
      } catch (err) {
        warn(`[StreamingStore] Live map history backfill failed for source ${sourceId}`, err);
      }
    }

    if (historicalAdded > 0) {
      this.historicalDataVersionSignal[1]((v) => v + 1);
    }
    if (anyRedisPoints || trackAdded > 0) {
      this.liveDataAppendVersionSignal[1]((v) => v + 1);
    }
    if (trackAdded > 0) {
      this.bumpTrackDataVersion();
    }
  }

  /**
   * Slow lane for live map tracks: fixed 2s history tail merge into liveTrackData only.
   * Boats/overlay stay on WebSocket; this does not bump liveDataAppendVersion.
   * @param queryEndMs Wall-clock end of tail window (defaults to Date.now()).
   */
  async refreshLiveMapTracksFromRedis(
    sourceIds: Set<number>,
    queryEndMs: number = Date.now()
  ): Promise<void> {
    if (sourceIds.size === 0) return;

    if (this.liveTrackRefreshInFlight) {
      this.pendingLiveTrackRefresh = { sourceIds, queryEndMs };
      this.logTrackRefreshSkippedBecauseInFlight();
      return;
    }

    const run = this.refreshLiveMapTracksTailImpl(sourceIds, queryEndMs);
    this.liveTrackRefreshInFlight = run;
    try {
      await run;
    } finally {
      this.liveTrackRefreshInFlight = null;
      const pending = this.pendingLiveTrackRefresh;
      this.pendingLiveTrackRefresh = null;
      if (pending) {
        void this.refreshLiveMapTracksFromRedis(pending.sourceIds, pending.queryEndMs);
      }
    }
  }

  /** @internal Exposed for diagnostics/tests. */
  isLiveTrackRefreshInFlight(): boolean {
    return this.liveTrackRefreshInFlight != null;
  }

  /** Latest per-source track refresh diagnostics from the most recent completed refresh. */
  getLastTrackRefreshDiagnostics() {
    return this.lastTrackRefreshDiagnostics;
  }

  /** Latest timestamp in the history-only live track catalog. */
  getLiveTrackLatestTimestamp(sourceIds: Set<number>): number | null {
    let latestTimestamp: number | null = null;

    for (const sourceId of sourceIds) {
      const last = this.getLastLiveTrackTimestampMs(sourceId);
      if (last != null && last > 0 && (latestTimestamp == null || last > latestTimestamp)) {
        latestTimestamp = last;
      }
    }

    return latestTimestamp;
  }

  private async refreshLiveMapTracksTailImpl(
    sourceIds: Set<number>,
    queryEndMs: number
  ): Promise<void> {
    const channels = [...LIVE_STREAM_CHANNELS, 'Datetime'];
    const sourceNames = Array.from(sourceIds)
      .map((sourceId) => ({ sourceId, sourceName: sourcesStore.getSourceName(sourceId) }))
      .filter((entry): entry is { sourceId: number; sourceName: string } => !!entry.sourceName);

    const queryWindow = computeLiveTrackTailQueryWindow(queryEndMs);
    const diagnostics: typeof this.lastTrackRefreshDiagnostics = [];

    const results = await Promise.all(
      sourceNames.map(async ({ sourceId, sourceName }) => {
        const existing = this.liveTrackData.get(sourceId);
        if (!existing?.length) {
          this.warnTrackRefreshBeforeSeed(sourceId);
          return {
            sourceId,
            queriedStartMs: queryWindow.queryStartMs,
            queriedEndMs: queryWindow.queryEndMs,
            addedPoints: 0,
            returnedPoints: 0,
            duplicatePointsSkipped: 0,
            minDeltaMs: null,
            maxDeltaMs: null,
            approxHz: null,
            skippedEmptyCatalog: true,
          };
        }

        try {
          const mergedData = await streamingDataService.fetchMergedData(
            sourceName,
            channels,
            queryWindow.queryStartMs,
            queryWindow.queryEndMs
          );

          const normalizedData = this.normalizeRedisTrackPoints(mergedData);
          const cadence = computeTrackSampleCadence(normalizedData);
          const addedPoints = this.mergeLiveTrackDataFromRedis(sourceId, normalizedData);
          const duplicatePointsSkipped = Math.max(0, cadence.returnedPoints - addedPoints);

          const entry = {
            sourceId,
            queriedStartMs: queryWindow.queryStartMs,
            queriedEndMs: queryWindow.queryEndMs,
            addedPoints,
            returnedPoints: cadence.returnedPoints,
            duplicatePointsSkipped,
            minDeltaMs: cadence.minDeltaMs,
            maxDeltaMs: cadence.maxDeltaMs,
            approxHz: cadence.approxHz,
            skippedEmptyCatalog: false,
          };

          return entry;
        } catch (err) {
          warn(`[StreamingStore] Live map track refresh failed for source ${sourceId}`, err);
          return null;
        }
      })
    );

    let totalAdded = 0;
    for (const entry of results) {
      if (!entry) continue;
      diagnostics.push(entry);
      totalAdded += entry.addedPoints;
    }

    this.lastTrackRefreshDiagnostics = diagnostics;

    if (totalAdded > 0) {
      this.bumpTrackDataVersion();
    }
  }

  /**
   * Get raw unfiltered data for chart display (bypasses all filtering)
   * @param sourceIds Set of source IDs to get data for
   * @returns Map of sourceId -> raw data array
   */
  getRawData(sourceIds: Set<number>, options?: { shallow?: boolean }): Map<number, any[]> {
    const result = new Map<number, any[]>();
    
    if (sourceIds.size === 0) {
      return result;
    }

    const shallow = options?.shallow === true;
    
    for (const sourceId of sourceIds) {
      const sourceData = this.historicalData.get(sourceId) || [];
      if (sourceData.length > 0) {
        result.set(sourceId, shallow ? sourceData : sourceData.map(p => ({ ...p })));
      }
    }
    
    return result;
  }

  /**
   * Live map render catalog: the history fetch seeds old context, WebSocket owns the visible tail.
   */
  getLiveRenderData(sourceIds: Set<number>, options?: { shallow?: boolean }): Map<number, any[]> {
    const result = new Map<number, any[]>();
    if (sourceIds.size === 0) return result;

    const shallow = options?.shallow === true;
    for (const sourceId of sourceIds) {
      const sourceData = this.liveRenderData.get(sourceId) || [];
      if (sourceData.length > 0) {
        result.set(sourceId, shallow ? sourceData : sourceData.map(p => ({ ...p })));
      }
    }
    return result;
  }

  /**
   * History-only live track catalog. Use this for LiveTrackLayer so track geometry is
   * not affected by WebSocket latest-point coalescing.
   */
  getLiveTrackData(sourceIds: Set<number>, options?: { shallow?: boolean }): Map<number, any[]> {
    const result = new Map<number, any[]>();
    if (sourceIds.size === 0) return result;

    const shallow = options?.shallow === true;
    for (const sourceId of sourceIds) {
      const sourceData = this.liveTrackData.get(sourceId) || [];
      if (sourceData.length > 0) {
        result.set(sourceId, shallow ? sourceData : sourceData.map(p => ({ ...p })));
      }
    }
    return result;
  }

  /**
   * Get filtered data based on selected sources, time window, and brush selection
   * @param sourceIds Set of source IDs to filter by
   * @param options Optional filtering options (selectedRange, selectedRanges)
   * @returns Map of sourceId -> filtered data array
   */
  getFilteredData(
    sourceIds: Set<number>,
    options?: {
      selectedRange?: any[];
      selectedRanges?: any[];
      effectivePlaybackTime?: Date | null;
      /** Live explore replay map: clip to time window only, never global selectionStore. */
      ignoreBrushSelection?: boolean;
      /** Live explore map: ignore cross-window timeWindow sync (e.g. maneuvers window at 0). */
      timeWindowMinutesOverride?: number;
    }
  ): Map<number, any[]> {
    const result = new Map<number, any[]>();

    if (sourceIds.size === 0) {
      return result;
    }

    // Use same reference time as chart (getDisplayWindowReferenceTime) when no override, so chart and map stay in sync
    const currentTime = options?.effectivePlaybackTime ?? getDisplayWindowReferenceTime() ?? selectedTime();
    const overrideTw = options?.timeWindowMinutesOverride;
    const currentTimeWindow =
      overrideTw != null && overrideTw > 0 ? Number(overrideTw) : Number(timeWindow()); // Minutes

    // Check for brush selection (live replay passes ignoreBrushSelection — e.g. after visiting live maneuvers)
    const ignoreBrush = options?.ignoreBrushSelection === true;
    const ranges = ignoreBrush ? [] : (options?.selectedRanges ?? selectedRanges());
    const singleRange = ignoreBrush ? [] : (options?.selectedRange ?? selectedRange());
    const hasBrushSelection = !ignoreBrush &&
      ((Array.isArray(ranges) && ranges.length > 0) ||
       (Array.isArray(singleRange) && singleRange.length > 0));

    // Helper to get timestamp from data point
    const getTimestamp = (d: any): number => {
      if (!d) return 0;
      const ts = d.timestamp || (d.Datetime instanceof Date ? d.Datetime.getTime() : new Date(d.Datetime).getTime());
      return Number.isFinite(ts) ? ts : 0;
    };

    for (const sourceId of sourceIds) {
      const sourceData = this.historicalData.get(sourceId) || [];
      if (sourceData.length === 0) {
        continue;
      }

      let filtered: any[];

      // Time window: binary search on sorted arrays (no per-point deep copy)
      if (!hasBrushSelection && currentTimeWindow > 0 && currentTime) {
        const windowMs = currentTimeWindow * 60 * 1000;
        const windowStartMs = currentTime.getTime() - windowMs;
        const windowEndMs = currentTime.getTime();
        const startIdx = StreamingStore.lowerBoundByTimestamp(sourceData, windowStartMs);
        const endIdx = StreamingStore.upperBoundByTimestamp(sourceData, windowEndMs);
        filtered = startIdx < endIdx ? sourceData.slice(startIdx, endIdx) : [];
      } else {
        filtered = sourceData;
      }

      // Brush selection (fleet/explore only — live replay passes empty ranges)
      if (hasBrushSelection) {
        const activeRanges: Array<{ start_time: Date | string; end_time: Date | string }> = [];

        if (Array.isArray(ranges) && ranges.length > 0) {
          activeRanges.push(...ranges);
        }

        if (activeRanges.length === 0 && Array.isArray(singleRange) && singleRange.length > 0) {
          activeRanges.push(...singleRange);
        }

        if (activeRanges.length > 0) {
          filtered = filtered.filter(d => {
            const timestampMs = getTimestamp(d);
            return activeRanges.some(range => {
              const startTime = range.start_time instanceof Date
                ? range.start_time.getTime()
                : new Date(range.start_time).getTime();
              const endTime = range.end_time instanceof Date
                ? range.end_time.getTime()
                : new Date(range.end_time).getTime();
              return timestampMs >= startTime && timestampMs <= endTime;
            });
          });
        }
      }

      if (filtered.length > 0) {
        // Shallow copy so consumers get a stable array per call without cloning every point
        result.set(sourceId, filtered === sourceData ? filtered.slice() : filtered);
      }
    }

    return result;
  }

  /**
   * Source IDs currently subscribed via WebSocket (fallback when liveSourcesStore is empty).
   */
  getSubscribedSourceIds(): Set<number> {
    return new Set(this.currentSourceIds);
  }

  /**
   * Get the latest timestamp across all sources
   * @param sourceIds Set of source IDs to check
   * @returns Latest timestamp in milliseconds, or null if no data
   */
  getLatestTimestamp(sourceIds: Set<number>): number | null {
    let latestTimestamp: number | null = null;
    
    for (const sourceId of sourceIds) {
      const catalogTs = this.getLastLocalCatalogTimestampMs(sourceId);
      if (catalogTs != null && catalogTs > 0 && (latestTimestamp === null || catalogTs > latestTimestamp)) {
        latestTimestamp = catalogTs;
      }
    }
    
    return latestTimestamp;
  }

  /**
   * Latest timestamp in the append-only live render catalog.
   * Use this for live map playback caps so the playhead does not outrun rendered data.
   */
  getLiveRenderLatestTimestamp(sourceIds: Set<number>): number | null {
    let latestTimestamp: number | null = null;

    for (const sourceId of sourceIds) {
      const sourceData = this.liveRenderData.get(sourceId) || [];
      if (sourceData.length === 0) continue;

      const lastPoint = sourceData[sourceData.length - 1];
      const ts = StreamingStore.pointTimestampMs(lastPoint);
      if (ts > 0 && (!latestTimestamp || ts > latestTimestamp)) {
        latestTimestamp = ts;
      }
    }

    return latestTimestamp;
  }

  /**
   * Cleanup resources
   */
  cleanup(): void {
    // Unsubscribe all callbacks
    for (const unsubscribe of this.unsubscribeCallbacks) {
      try {
        unsubscribe();
      } catch (err) {
        warn('[StreamingStore] Error unsubscribing callback:', err);
      }
    }
    this.unsubscribeCallbacks = [];

    // Disconnect WebSocket
    streamingService.setDataBatchHooks(null, null);
    streamingService.disconnect();

    this._isInitialized = false;
    this._isInitializing = false;
    this.currentSourceIds = new Set();
    this.loadingSignal[1](false);
    this.errorSignal[1](null);
    this.newDataSignal[1](new Map()); // Clear data signal
    this.historicalData.clear(); // Clear historical data
    this.liveRenderData.clear();
    this.liveTrackData.clear();
    this.liveTrackRefreshInFlight = null;
    this.pendingLiveTrackRefresh = null;
    this.liveBoatBackfillInFlight = null;
    this.pendingLiveBoatBackfill = null;
    this.lastLiveMapBackfillCompletedAt = 0;
    this.clearLiveBoatBackfillDeferredTimer();
    this.trackRefreshBeforeSeedWarned.clear();
    this.lastTrackRefreshDiagnostics = [];
    this.lastTrackRefreshSkippedLogAt = 0;
    this.liveDataAppendVersionSignal[1](0);
    this.trackDataVersionSignal[1](0);
    // Accepted and dropped points are read as a pair to tell a broken handler from a quiet feed,
    // so both reset per streaming session - a previous session's totals would muddy that.
    this.dataPointCount = 0;
    this.wsHandlingErrorCount = 0;
    this.streamingLifecycleSignal[1]((n) => n + 1);
  }
}

// Singleton instance
export const streamingStore = new StreamingStore();
